"use client";
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail, Phone, Download, Rocket, ChevronRight, Star, TrendingUp, Users, BarChart3,
  Code2, Palette, MessageSquare, Package, Shield, Clock, ExternalLink,
  CheckCircle2, ArrowUpRight, Sparkles, Brain, Heart, Layers, Home, X
} from "lucide-react";

/* ============== THEME & HELPERS ============== */
const THEME = {
  primaryFrom: "from-blue-600",
  primaryTo: "to-purple-600",
  surface: "bg-black/70",
  stroke: "border-white/15",
  focus: "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/70",
};
const cn = (...classes) => classes.filter(Boolean).join(" ");

/* Helper: copy to clipboard with fallback */
function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
  return Promise.resolve();
}

/* ============== DATA ============== */
const PROFILE = {
  name: "รัชชานนท์ แตงสี",
  nameEN: "Rachanon Taengsee",
  title: "Sales & Marketing Lead",
  subtitle: "E-commerce Ops • API Integration • Creator Program",
  blurb:
    "ดูแลงานอีคอมเมิร์ซครบวงจร (Marketplace • API • Support • Content • QC) เน้นระบบและข้อมูลขับเคลื่อนผลลัพธ์",
  company: "เอสพีที เทรดดิ้ง 2015 จำกัด",
  companyUrl: "https://www.supertopgreenhouse.com/",
  period: "13 ธ.ค. 2567 – 25 พ.ค. 2568",
  duration: "5 เดือน 12 วัน",
  periodUrl:
    "https://www.canva.com/design/DAGlVDZ5LaU/60bAKhvYQ-waEbJHJoSXrg/edit?utm_content=DAGlVDZ5LaU&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton",
  location: "อำเภอคลองหลวง จังหวัดปทุมธานี",
  locationUrl: "https://maps.app.goo.gl/oKMyPhMrnZDhUAgh6",
  email: "rachanon629@gmail.com",
  phonePrimary: "098-391-0768",
  phoneSecondary: "062-564-4079",
  line: "fluck5201314",
  lineUrl: "https://line.me/ti/p/vyQVsSqpj",
  tiktok: "@fluckkkkkkkkkk",
  tiktokUrl: "https://www.tiktok.com/@fluckkkkkkkkk?is_from_webapp=1&sender_device=pc",
  resumeBoth:
    "https://www.canva.com/design/DAGEnoEf1YM/C41X8GGCyFkqumC2Jx6c6A/edit?utm_content=DAGEnoEf1YM&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton",
};

const ACHIEVEMENTS = [
  { icon: <TrendingUp />, value: "20+", label: "New Partners", desc: "Creators & Affiliates" },
  { icon: <Users />, value: "3", label: "Platforms", desc: "Managed Channels" },
  { icon: <BarChart3 />, value: "95%", label: "Stock Accuracy", desc: "via API sync" },
  { icon: <Star />, value: "4.2/5", label: "Customer Rating", desc: "Service Quality" },
];

const SKILLS = [
  {
    category: "E-commerce & Sales",
    icon: <Package className="w-5 h-5" />,
    gradient: "from-blue-500 to-cyan-500",
    skills: [
      { name: "Lnwshop Management", level: 75 },
      { name: "TikTok Shop Operations", level: 70 },
      { name: "Marketplace Strategy", level: 65 },
      { name: "Sales Analytics", level: 68 },
    ],
  },
  {
    category: "API & Technical",
    icon: <Code2 className="w-5 h-5" />,
    gradient: "from-purple-500 to-pink-500",
    skills: [
      { name: "API Integration (Dsetsoft)", level: 72 },
      { name: "Workflow Automation", level: 65 },
      { name: "Data Synchronization", level: 70 },
      { name: "System Architecture", level: 60 },
    ],
  },
  {
    category: "Marketing & Content",
    icon: <Palette className="w-5 h-5" />,
    gradient: "from-orange-500 to-red-500",
    skills: [
      { name: "Content Creation (Canva)", level: 78 },
      { name: "Campaign Planning", level: 65 },
      { name: "Brand CI Management", level: 62 },
      { name: "Creator / Affiliate", level: 70 },
    ],
  },
  {
    category: "Operations & QC",
    icon: <Shield className="w-5 h-5" />,
    gradient: "from-green-500 to-emerald-500",
    skills: [
      { name: "Quality Control", level: 73 },
      { name: "Inventory Management", level: 68 },
      { name: "Process Optimization", level: 65 },
      { name: "Data Reporting", level: 70 },
    ],
  },
];

const PROJECTS = [
  {
    id: 1,
    title: "Marketplace API Integration",
    category: "Technical",
    icon: <Code2 />,
    color: "from-purple-600 to-blue-600",
    impact: "+20% Efficiency",
    description: "พัฒนา workflow ดึงคำสั่งซื้อแบบ Real-time และซิงก์สต๊อกข้ามแพลตฟอร์ม",
    features: [
      "Automated order sync",
      "Real-time stock update",
      "Error handling & recovery",
      "Basic monitoring dashboard",
    ],
    technologies: ["Dsetsoft API", "Webhook", "Data Pipeline", "Analytics"],
    results: "ลดเวลาประมวลผล 30% และลด error การอัปเดตสต๊อก",
  },
  {
    id: 2,
    title: "TikTok Creator Network",
    category: "Marketing",
    icon: <Users />,
    color: "from-pink-600 to-orange-600",
    impact: "50+ Creators",
    description: "สร้างและดูแลเครือข่าย Creator / Affiliate บน TikTok Shop",
    features: [
      "Recruitment & onboarding",
      "Basic performance tracking",
      "Incentive program",
      "Content collaboration",
    ],
    technologies: ["TikTok Shop", "Affiliate Platform", "Analytics", "CRM"],
    results: "ยอดขายผ่าน Affiliate +35% ภายใน 3 เดือน",
  },
  {
    id: 3,
    title: "Quality Control System",
    category: "Operations",
    icon: <Shield />,
    color: "from-green-600 to-teal-600",
    impact: "95% Accuracy",
    description: "ระบบ QC ครบวงจร พร้อมติดตามแบบ Real-time",
    features: [
      "Germination rate tracking",
      "Production planning calc",
      "Quality metrics dashboard",
      "Research center coordination",
    ],
    technologies: ["QC Tools", "Data Analytics", "Reporting", "Integration"],
    results: "ลดข้อร้องเรียนคุณภาพ 40% และปรับปรุงกระบวนการ",
  },
];

const TIMELINE = [
  { date: "ธ.ค. 2567", event: "เริ่มงานหัวหน้าฝ่ายขายและการตลาด", type: "work" },
  { date: "ม.ค. 2568", event: "เริ่มใช้ระบบดึงข้อมูล API", type: "achievement" },
  { date: "ก.พ. 2568", event: "สร้างเครือข่ายพาร์ทเนอร์ 20+ ราย", type: "achievement" },
  { date: "มี.ค. 2568", event: "ปรับปรุงระบบควบคุมคุณภาพ", type: "project" },
  { date: "เม.ย. 2568", event: "พัฒนาระบบรายงานข้อมูล", type: "project" },
  { date: "พ.ค. 2568", event: "ขยายช่องทางการขาย", type: "current" },
];

/* ============== HOOKS/UTILS ============== */
function useSectionSpy(ids, rootMargin = "-35% 0px -55% 0px") {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        let top = null;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const ratio = entry.intersectionRatio;
            if (!top || ratio > top.ratio) top = { id: entry.target.id, ratio };
          }
        }
        if (top) setActive(top.id);
      },
      { root: null, threshold: 0.35, rootMargin }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [ids, rootMargin]);
  return active;
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);
  return reduced;
}

/* ============== COUNTER ============== */
function Counter({ value, suffix = "" }) {
  const numericValue = useMemo(
    () => parseInt(value.toString().replace(/\D/g, ""), 10) || 0,
    [value]
  );
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = null;
    const duration = 900;
    let rafId = 0;
    const step = (ts) => {
      if (start === null) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      setCount(Math.floor(numericValue * (1 - Math.pow(1 - p, 2))));
      if (p < 1) rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [numericValue]);
  return (
    <span>
      {count}
      {suffix}
    </span>
  );
}

/* ============== PARTICLE (SAFE) ============== */
function ParticleField() {
  return null;
}

/* ============== MAIN ============== */
export default function EnhancedPortfolioPro() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [particleIntensity, setParticleIntensity] = useState("medium");
  const [copiedPhone, setCopiedPhone] = useState(null); // 'primary' | 'secondary' | null

  const sections = [
    { id: "hero", label: "หน้าแรก", icon: <Home className="w-4 h-4" /> },
    { id: "personal", label: "ข้อมูล", icon: <MessageSquare className="w-4 h-4" /> },
    { id: "skills", label: "ทักษะ", icon: <Brain className="w-4 h-4" /> },
    { id: "projects", label: "ผลงาน", icon: <Layers className="w-4 h-4" /> },
    { id: "timeline", label: "Timeline", icon: <Clock className="w-4 h-4" /> },
    { id: "contact", label: "ติดต่อ", icon: <MessageSquare className="w-4 h-4" /> },
  ];

  const spyActive = useSectionSpy(sections.map((s) => s.id));
  const [manualActive, setManualActive] = useState(null);
  const activeSection = manualActive ?? spyActive;

  const GMAIL_COMPOSE_URL = useMemo(() => {
    const subject = "สอบถามตำแหน่งงาน - Sales & Marketing / E-commerce";
    const body = `สวัสดีครับคุณรัชชานนท์,\n\nผม/ดิฉัน [ชื่อของคุณ] จาก [ชื่อบริษัท]\nสนใจพูดคุยโอกาสทำงานร่วมกัน\n\nตำแหน่ง: [ระบุตำแหน่ง]\nรายละเอียด: [รายละเอียดเพิ่มเติม]\n\nสะดวกติดต่อกลับที่\nEmail: [email ของคุณ]\nTel: [เบอร์ของคุณ]\n\nขอบคุณครับ/ค่ะ\n[ชื่อของคุณ]`;
    return `https://mail.google.com/mail/?view=cm&fs=1&to=${PROFILE.email}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, []);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = "auto";
    };
  }, []);

  const handleCopy = (which) => {
    const num = which === "primary" ? PROFILE.phonePrimary : PROFILE.phoneSecondary;
    copyToClipboard(num).then(() => {
      setCopiedPhone(which);
      setTimeout(() => setCopiedPhone(null), 1400);
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white overflow-x-hidden">
      {/* Particle BG */}
      <ParticleField />

      {/* NAVBAR */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-slate-900 to-black backdrop-blur-xl border-b border-white/10">
        <nav className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" aria-label="Primary">
          <button
            className="flex items-center gap-2"
            aria-label="Scroll to top"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <span className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 grid place-items-center">
              <Rocket className="w-6 h-6" />
            </span>
            <span className="hidden sm:block">
              <span className="block font-bold text-lg">{PROFILE.name}</span>
              <span className="block text-xs text-blue-300">Portfolio 2025</span>
            </span>
          </button>
          <div className="flex items-center gap-1 md:gap-2">
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  setManualActive(s.id);
                  document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                  window.history.replaceState(null, "", `#${s.id}`);
                  setTimeout(() => setManualActive(null), 900);
                }}
                aria-current={activeSection === s.id ? "page" : undefined}
                className={cn(
                  "flex items-center gap-1 px-2 sm:px-4 py-2.5 rounded-xl font-semibold transition-all",
                  THEME.focus,
                  activeSection === s.id
                    ? "bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg shadow-blue-500/30"
                    : "bg-white/10 hover:bg-white/20 border border-white/20"
                )}
              >
                {s.icon}
                <span className="hidden md:inline text-sm lg:text-base">{s.label}</span>
                <span className="inline md:hidden text-xs">{s.label.slice(0, 3)}</span>
              </a>
            ))}
          </div>
        </nav>
      </header>

      {/* Spacer */}
      <div className="h-16" />

      {/* HERO */}
      <section id="hero" className="relative min-h-[92vh] flex items-center justify-center px-4 scroll-mt-20">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/15 to-purple-500/15 border border-blue-500/30 mb-6">
              <Sparkles className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-blue-300">Available for New Opportunities</span>
            </span>
            <h1 className="text-5xl md:text-7xl font-black mb-2">{PROFILE.name}</h1>
            <p className="text-xl md:text-2xl font-semibold mb-1">{PROFILE.title}</p>
            <p className="text-lg text-slate-300">{PROFILE.subtitle}</p>
          </motion.div>

          <p className="text-lg text-slate-300 max-w-3xl mx-auto mt-6">{PROFILE.blurb}</p>

          {/* 3 ปุ่มเป็นลิงก์ */}
          <div className="flex flex-wrap gap-3 justify-center mt-6">
            <a
              href={PROFILE.companyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-blue-500/15 rounded-full border border-blue-500/30 text-blue-200 text-sm hover:bg-blue-500/20 transition"
              aria-label="เปิดเว็บไซต์บริษัท"
            >
              💼 {PROFILE.company} <ExternalLink className="inline-block w-3.5 h-3.5 ml-1 align-[-2px]" />
            </a>
            <a
              href={PROFILE.periodUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-slate-800 rounded-full border border-slate-700 text-sm hover:bg-slate-700 transition"
              aria-label="เปิดลิงก์ช่วงเวลา"
            >
              📅 {PROFILE.period} ({PROFILE.duration}) <ExternalLink className="inline-block w-3.5 h-3.5 ml-1 align-[-2px]" />
            </a>
            <a
              href={PROFILE.locationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-slate-800 rounded-full border border-slate-700 text-sm hover:bg-slate-700 transition"
              aria-label="เปิดแผนที่ Google Maps"
            >
              📍 {PROFILE.location} <ExternalLink className="inline-block w-3.5 h-3.5 ml-1 align-[-2px]" />
            </a>
          </div>

          <div className="flex flex-wrap gap-4 justify-center mt-8">
            <a href={PROFILE.resumeBoth} target="_blank" rel="noopener noreferrer" className="group" aria-label="Download resume">
              <button
                className={cn(
                  "flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all hover:scale-105",
                  "bg-gradient-to-r",
                  THEME.primaryFrom,
                  THEME.primaryTo
                )}
              >
                <Download className="w-5 h-5" />
                Download Resume (TH/EN)
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </button>
            </a>
            <a href={GMAIL_COMPOSE_URL} target="_blank" rel="noopener noreferrer" className="group" aria-label="Send email">
              <button
                className={cn(
                  "flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all border border-slate-700 hover:bg-slate-700",
                  THEME.focus,
                  "bg-slate-800"
                )}
              >
                <Mail className="w-5 h-5" />
                Send Email Now
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
            {ACHIEVEMENTS.map((a, i) => (
              <div key={i} className={cn(THEME.surface, "backdrop-blur-sm rounded-2xl p-6 text-center border", THEME.stroke)}>
                <span className="inline-grid place-items-center w-12 h-12 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 mb-3">
                  {React.cloneElement(a.icon, { className: "w-6 h-6 text-blue-400" })}
                </span>
                <div className="text-2xl font-bold mb-1">
                  <Counter
                    value={a.value}
                    suffix={a.value.includes("%") ? "%" : a.value.includes("+") ? "+" : ""}
                  />
                </div>
                <div className="text-sm text-slate-300">{a.label}</div>
                <div className="text-xs text-slate-400 mt-1">{a.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PERSONAL */}
      <section id="personal" className="py-16 px-4 bg-slate-950/50 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-block px-8 py-4 rounded-2xl border-2 border-white/15 bg-black/70">
              <h2 className="text-4xl font-black">ข้อมูลส่วนตัว / Personal Information</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <article className={cn(THEME.surface, "rounded-2xl p-6 border", THEME.stroke)}>
              <h3 className="text-xl font-bold mb-4 border-b border-white/10 pb-2">📋 ข้อมูลพื้นฐาน</h3>
              <ul className="space-y-3">
                <li>
                  <span className="text-blue-300 text-sm font-semibold">ชื่อ-นามสกุล</span>
                  <p className="text-lg">{PROFILE.name}</p>
                  <p className="text-slate-300">{PROFILE.nameEN}</p>
                </li>
                <li>
                  <span className="text-blue-300 text-sm font-semibold">ตำแหน่งปัจจุบัน</span>
                  <p className="text-lg">{PROFILE.title}</p>
                </li>
                <li>
                  <span className="text-blue-300 text-sm font-semibold">บริษัท</span>
                  <p className="text-lg">{PROFILE.company}</p>
                </li>
                <li>
                  <span className="text-blue-300 text-sm font-semibold">ระยะเวลาทำงาน</span>
                  <p className="text-lg">{PROFILE.duration}</p>
                </li>
              </ul>
            </article>

            <article className={cn(THEME.surface, "rounded-2xl p-6 border", THEME.stroke)}>
              <h3 className="text-xl font-bold mb-4 border-b border-white/10 pb-2">📞 ช่องทางติดต่อ</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-green-300 text-sm font-semibold">Email</span>
                  <p className="text-lg break-all">{PROFILE.email}</p>
                </div>
                <div>
                  <span className="text-green-300 text-sm font-semibold">Phone</span>
                  <p className="text-lg">
                    {PROFILE.phonePrimary} <span className="text-xs text-slate-400">(Primary)</span>
                  </p>
                  <p className="text-lg">
                    {PROFILE.phoneSecondary} <span className="text-xs text-slate-400">(Secondary)</span>
                  </p>
                </div>
                <div>
                  <span className="text-green-300 text-sm font-semibold">LINE</span>
                  <p className="text-lg">{PROFILE.line}</p>
                </div>
                <div>
                  <span className="text-pink-300 text-sm font-semibold">TikTok</span>
                  <p className="text-lg">{PROFILE.tiktok}</p>
                </div>
              </div>
            </article>

            <article className={cn(THEME.surface, "rounded-2xl p-6 border", THEME.stroke)}>
              <h3 className="text-xl font-bold mb-4 border-b border-white/10 pb-2">🎓 การศึกษา</h3>
              <ul className="space-y-3">
                <li>
                  <span className="text-purple-300 text-sm font-semibold">วุฒิการศึกษา</span>
                  <p className="text-lg">วิศวกรรมศาสตรบัณฑิต</p>
                </li>
                <li>
                  <span className="text-purple-300 text-sm font-semibold">สาขา</span>
                  <p className="text-lg">วิศวกรรมเครื่องมือและแม่พิมพ์</p>
                </li>
                <li>
                  <span className="text-purple-300 text-sm font-semibold">สถาบัน</span>
                  <p>มหาวิทยาลัยเทคโนโลยีราชมงคลพระนคร</p>
                </li>
                <li>
                  <span className="text-purple-300 text-sm font-semibold">GPA</span>
                  <p className="text-lg">3.44 (ภาคสุดท้าย 3.83)</p>
                </li>
                <li>
                  <span className="text-purple-300 text-sm font-semibold">ปีที่จบ</span>
                  <p className="text-lg">พฤษภาคม 2567</p>
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* SKILLS */}
      <section id="skills" className="py-20 px-4 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-block px-8 py-4 rounded-2xl border-2 border-white/15 bg-black/70">
              <h2 className="text-4xl font-black">Technical Expertise</h2>
              <p className="text-slate-300 mt-2">E-commerce • Technical • Marketing</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {SKILLS.map((cat, idx) => (
              <article
                key={idx}
                className={cn(THEME.surface, "rounded-2xl p-6 border hover:border-white/30 transition-all", THEME.stroke)}
              >
                <div className="flex items-center gap-3 mb-6">
                  <span className={cn("w-10 h-10 rounded-lg bg-gradient-to-br grid place-items-center shadow-lg", cat.gradient)}>
                    {cat.icon}
                  </span>
                  <h3 className="text-xl font-bold">{cat.category}</h3>
                </div>
                <ul className="space-y-4" aria-label={`${cat.category} skills`}>
                  {cat.skills.map((s, i) => (
                    <li key={i}>
                      <div className="flex justify-between mb-2 text-slate-300 text-sm">
                        <span>{s.name}</span>
                        <span>{s.level}%</span>
                      </div>
                      <div className="h-2 bg-slate-800 rounded-full overflow-hidden" aria-hidden>
                        <div
                          className={cn("h-full bg-gradient-to-r rounded-full shadow-md", cat.gradient)}
                          style={{ width: `${s.level}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="py-20 px-4 bg-slate-950/50 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-block px-8 py-4 rounded-2xl border-2 border-white/15 bg-black/70">
              <h2 className="text-4xl font-black">Featured Projects</h2>
              <p className="text-slate-300 mt-2">Real-world solutions with measurable impact</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PROJECTS.map((p) => (
              <article
                key={p.id}
                onClick={() => setSelectedProject(p)}
                className="cursor-pointer rounded-2xl p-6 border border-white/15 bg-black/70 hover:border-white/30 hover:scale-[1.02] transition-all"
              >
                <div className={cn("w-12 h-12 rounded-xl bg-gradient-to-br grid place-items-center mb-4", p.color)}>
                  {p.icon}
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-300 uppercase tracking-wide font-semibold">{p.category}</span>
                  <span className="text-sm font-bold text-green-400">{p.impact}</span>
                </div>
                <h3 className="text-xl font-bold mb-3">{p.title}</h3>
                <p className="text-sm text-slate-200 mb-4">{p.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {p.technologies.slice(0, 3).map((t, i) => (
                    <span key={i} className="px-2 py-1 text-xs bg-white/10 border border-white/20 rounded-lg">
                      {t}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-blue-400">
                  <span className="text-sm font-semibold">View Details</span>
                  <ArrowUpRight className="w-4 h-4" />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section id="timeline" className="py-20 px-4 scroll-mt-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-block px-8 py-4 rounded-2xl border-2 border-white/15 bg-black/70">
              <h2 className="text-4xl font-black">Professional Journey</h2>
              <p className="text-slate-300 mt-2">Milestones & achievements</p>
            </div>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-blue-500 to-purple-500" />
            {TIMELINE.map((t, i) => (
              <div key={i} className={cn("relative flex items-center mb-8", i % 2 === 0 ? "justify-start" : "justify-end")}>
                <div className={cn("w-5/12", i % 2 === 0 ? "text-right pr-8" : "text-left pl-8")}>
                  <div className="rounded-xl p-4 border border-white/15 bg-black/70">
                    <div className="text-sm text-blue-400 font-semibold mb-1">{t.date}</div>
                    <div className="font-medium">{t.event}</div>
                    <span
                      className={cn(
                        "inline-block mt-2 px-3 py-1 rounded-full text-xs font-bold border",
                        t.type === "achievement" && "bg-green-500/20 text-green-300 border-green-400/40",
                        t.type === "project" && "bg-orange-500/20 text-orange-300 border-orange-400/40",
                        t.type === "current" && "bg-blue-500/20 text-blue-300 border-blue-400/40",
                        t.type === "work" && "bg-purple-500/20 text-purple-300 border-purple-400/40"
                      )}
                    >
                      {t.type}
                    </span>
                  </div>
                </div>
                <div className="absolute left-1/2 -translate-x-1/2 w-4 h-4 bg-white rounded-full border-4 border-black shadow-[0_0_20px_rgba(255,255,255,0.8)]" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-20 px-4 bg-slate-950/50 scroll-mt-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-block px-8 py-4 rounded-2xl border-2 border-white/15 bg-black/70">
              <h2 className="text-4xl font-black">Let's Connect</h2>
              <p className="text-slate-300 mt-2">Ready to bring value to your organization</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <a href={GMAIL_COMPOSE_URL} target="_blank" rel="noopener noreferrer" className="group">
              <div className="flex items-center gap-4 p-4 rounded-xl border border-white/15 bg-slate-900/50 hover:bg-slate-800/50 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/20 to-purple-500/20 grid place-items-center">
                  <Mail className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-1">Email</div>
                  <div className="text-sm font-medium group-hover:text-blue-400 transition-colors">{PROFILE.email}</div>
                </div>
                <ExternalLink className="w-4 h-4 text-slate-500 group-hover:text-blue-400 ml-auto" />
              </div>
            </a>

            {/* Primary phone: copy number instead of tel: */}
            <button
              type="button"
              onClick={() => handleCopy("primary")}
              className="group text-left"
              aria-label="Copy primary phone number"
            >
              <div className="flex items-center gap-4 p-4 rounded-xl border border-white/15 bg-slate-900/50 hover:bg-slate-800/50 transition-colors cursor-pointer">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500/20 to-emerald-500/20 grid place-items-center">
                  {copiedPhone === "primary" ? (
                    <CheckCircle2 className="w-6 h-6 text-green-400" />
                  ) : (
                    <Phone className="w-6 h-6 text-green-400" />
                  )}
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-1">Phone</div>
                  <div className="text-sm font-medium transition-colors">
                    {PROFILE.phonePrimary} <span className="text-xs text-slate-400">(Primary)</span>
                  </div>
                </div>
                <span className="ml-auto text-xs">
                  {copiedPhone === "primary" ? "Copied!" : "Tap to copy"}
                </span>
              </div>
            </button>

            {/* Secondary phone: copy number as well */}
            <button
              type="button"
              onClick={() => handleCopy("secondary")}
              className="group text-left"
              aria-label="Copy secondary phone number"
            >
              <div className="flex items-center gap-4 p-4 rounded-xl border border-white/15 bg-slate-900/50 hover:bg-slate-800/50 transition-colors cursor-pointer">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500/20 to-emerald-500/20 grid place-items-center">
                  {copiedPhone === "secondary" ? (
                    <CheckCircle2 className="w-6 h-6 text-green-400" />
                  ) : (
                    <Phone className="w-6 h-6 text-green-400" />
                  )}
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-1">Phone</div>
                  <div className="text-sm font-medium transition-colors">
                    {PROFILE.phoneSecondary} <span className="text-xs text-slate-400">(Secondary)</span>
                  </div>
                </div>
                <span className="ml-auto text-xs">
                  {copiedPhone === "secondary" ? "Copied!" : "Tap to copy"}
                </span>
              </div>
            </button>

            <a href={PROFILE.lineUrl} target="_blank" rel="noopener noreferrer" className="group">
              <div className="flex items-center gap-4 p-4 rounded-2xl border border-white/15 bg-slate-900/50 hover:bg-slate-800/50 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500/20 to-lime-500/20 grid place-items-center">
                  <MessageSquare className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-1">LINE</div>
                  <div className="text-sm font-medium group-hover:text-green-400 transition-colors">{PROFILE.line}</div>
                </div>
                <ExternalLink className="w-4 h-4 text-slate-500 group-hover:text-green-400 ml-auto" />
              </div>
            </a>

            <a href={PROFILE.tiktokUrl} target="_blank" rel="noopener noreferrer" className="group">
              <div className="flex items-center gap-4 p-4 rounded-xl border border-white/15 bg-slate-900/50 hover:bg-slate-800/50 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-pink-500/20 to-rose-500/20 grid place-items-center">
                  <Heart className="w-6 h-6 text-pink-400" />
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-1">TikTok</div>
                  <div className="text-sm font-medium group-hover:text-pink-400 transition-colors">{PROFILE.tiktok}</div>
                </div>
                <ExternalLink className="w-4 h-4 text-slate-500 group-hover:text-pink-400 ml-auto" />
              </div>
            </a>
          </div>

          <div className="text-center">
            <a href={GMAIL_COMPOSE_URL} target="_blank" rel="noopener noreferrer">
              <button
                className={cn(
                  "px-8 py-3 rounded-full font-medium transition-all hover:scale-105 bg-gradient-to-r",
                  THEME.primaryFrom,
                  THEME.primaryTo
                )}
              >
                Send Email Now
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 px-4 border-t border-slate-800">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-slate-300 text-sm mb-2">
            © 2025 {PROFILE.name}. Crafted with passion and precision.
          </p>
          <div className="flex items-center justify-center gap-4 text-xs text-slate-300">
            <span>React</span>
            <span>•</span>
            <span>Tailwind CSS</span>
            <span>•</span>
            <span>Framer Motion</span>
            <button
              onClick={() =>
                setParticleIntensity((p) => (p === "high" ? "low" : p === "low" ? "medium" : "high"))
              }
              className="hover:text-blue-400 transition-colors"
              aria-label="Toggle particle intensity"
            >
              Particles: {particleIntensity}
            </button>
          </div>
        </div>
      </footer>

      {/* PROJECT MODAL */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProject(null)}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 grid place-items-center p-4"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={selectedProject.title}
              className="bg-slate-900 border border-slate-800 rounded-2xl max-w-2xl w-full max-h-[82vh] overflow-y-auto p-8"
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div
                    className={cn(
                      "w-16 h-16 rounded-xl bg-gradient-to-br grid place-items-center mb-4",
                      selectedProject.color
                    )}
                  >
                    {selectedProject.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-2">{selectedProject.title}</h3>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-slate-400">{selectedProject.category}</span>
                    <span className="text-green-400 font-semibold">{selectedProject.impact}</span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="text-slate-400 hover:text-white"
                  aria-label="Close project dialog"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <p className="text-slate-300 mb-6">{selectedProject.description}</p>

              <div className="mb-6">
                <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Key Features
                </h4>
                <ul className="space-y-2">
                  {selectedProject.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5" />
                      <span className="text-slate-300">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-6">
                <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Technologies Used
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedProject.technologies.map((t, i) => (
                    <span key={i} className="px-3 py-1 bg-slate-800 rounded-lg text-sm text-slate-300">
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-slate-700">
                <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  Results
                </h4>
                <p className="text-slate-300">{selectedProject.results}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
